/**
 * Een foto verkleinen vóór hij in de wachtrij gaat.
 *
 * Een telefoonfoto is 3 tot 6 MB en 4000 px breed; de server maakt er toch een
 * weergave van hoogstens 1600 px van (rapport en klantpagina) en een miniatuur
 * van 400 px, en gebruikt het origineel alleen als terugval. Wat over de lijn gaat
 * is dus gemiddeld tien keer kleiner, zonder dat het rapport er iets van merkt.
 *
 * - De langste zijde wordt hoogstens {@link PHOTO_MAX_EDGE} px, als JPEG op
 *   kwaliteit {@link PHOTO_QUALITY}. De browser draait de foto daarbij rechtop
 *   volgens zijn EXIF-oriëntatie.
 * - HEIC: kan de browser hem lezen (Safari), dan gaat hij als JPEG; kan dat niet
 *   (Chrome op een Mac), dan gaat het origineel en zet de server hem om.
 * - Een foto die al klein genoeg is, blijft onaangeroerd - met zijn EXIF.
 * - Lukt iets niet (geen geheugen, een kapot bestand), dan gaat het origineel:
 *   verkleinen is winst, nooit een reden om een foto te weigeren.
 */

export const PHOTO_MAX_EDGE = 2000;
export const PHOTO_QUALITY = 0.82;

/** Een JPEG of WebP tot deze grootte en binnen de maat gaat ongewijzigd. */
const SMALL_ENOUGH_BYTES = 1_500_000;

export interface ShrunkPhoto {
    blob: Blob;
    mimeType: string;
    fileName: string;
}

export async function shrinkPhoto(
    blob: Blob,
    mimeType: string,
    fileName: string,
): Promise<ShrunkPhoto> {
    const original: ShrunkPhoto = { blob, mimeType, fileName };

    if (typeof createImageBitmap !== 'function') {
        return original;
    }

    let bitmap: ImageBitmap;

    try {
        bitmap = await createImageBitmap(blob, { imageOrientation: 'from-image' });
    } catch {
        return original;
    }

    try {
        const longest = Math.max(bitmap.width, bitmap.height);
        const keepsFormat = mimeType === 'image/jpeg' || mimeType === 'image/webp';

        if (
            longest === 0 ||
            (longest <= PHOTO_MAX_EDGE && keepsFormat && blob.size <= SMALL_ENOUGH_BYTES)
        ) {
            return original;
        }

        const scale = Math.min(1, PHOTO_MAX_EDGE / longest);
        const width = Math.max(1, Math.round(bitmap.width * scale));
        const height = Math.max(1, Math.round(bitmap.height * scale));
        const encoded = await encodeJpeg(bitmap, width, height);

        /* Niet kleiner geworden en geen formaat dat de server moet omzetten: dan het origineel. */
        if (!encoded || (keepsFormat && scale === 1 && encoded.size >= blob.size)) {
            return original;
        }

        return { blob: encoded, mimeType: 'image/jpeg', fileName: asJpegName(fileName) };
    } catch {
        return original;
    } finally {
        bitmap.close();
    }
}

async function encodeJpeg(
    bitmap: ImageBitmap,
    width: number,
    height: number,
): Promise<Blob | null> {
    if (typeof OffscreenCanvas === 'function') {
        const canvas = new OffscreenCanvas(width, height);
        const context = canvas.getContext('2d');

        if (!context) {
            return null;
        }

        draw(context, bitmap, width, height);

        return canvas.convertToBlob({ type: 'image/jpeg', quality: PHOTO_QUALITY });
    }

    if (typeof document === 'undefined') {
        return null;
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');

    if (!context) {
        return null;
    }

    draw(context, bitmap, width, height);

    return new Promise((resolve) =>
        canvas.toBlob((result) => resolve(result), 'image/jpeg', PHOTO_QUALITY),
    );
}

/** JPEG kent geen transparantie: een PNG met een doorzichtige rand krijgt wit, geen zwart. */
function draw(
    context: OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D,
    bitmap: ImageBitmap,
    width: number,
    height: number,
): void {
    context.fillStyle = 'white';
    context.fillRect(0, 0, width, height);
    context.imageSmoothingQuality = 'high';
    context.drawImage(bitmap, 0, 0, width, height);
}

/** `IMG_0412.HEIC` → `IMG_0412.jpg`; een naam die al op .jpg of .jpeg eindigt, blijft. */
export function asJpegName(fileName: string): string {
    if (/\.jpe?g$/i.test(fileName)) {
        return fileName;
    }

    const dot = fileName.lastIndexOf('.');

    return `${dot > 0 ? fileName.slice(0, dot) : fileName}.jpg`;
}
